import * as React from 'react';
import { ReactElement, RefObject, useEffect, useRef, useState } from 'react';

import cn from 'classnames';
import { Resizable } from 're-resizable';

import { Alert, Tabs } from '@components/common';
import { PageBody, PageLeft, PageRight } from '@components/RepoSessionApp/Layout';
import { SessionContext } from '@components/RepoSessionApp/SessionPage';
import { SplitPageSVG } from '@components/svgs';
import { compact } from '@components/utils';
import { UseVideoPlayer } from '@components/VideoPlayer';
import { useDeviceType } from '@hooks/useDeviceType';
import { useLocalStorage } from '@hooks/useLocalStorage';

type Props = {
  renderPageLeft: (v: { restrictWidth: boolean }) => ReactElement;
  renderHeader: () => ReactElement;
  renderTabContent: (activeTab: string, onScroll: () => void, tabWrapperRef: RefObject<any>) => ReactElement;
  activeTab: string;
  setActiveTab: (v: string) => void;
  owner?: User;
  candidate: Candidate | null;
  liveStreamLocked?: boolean;
};
export const SessionPageLayout: React.FC<React.PropsWithChildren<Props>> = ({
  renderPageLeft,
  owner,
  candidate,
  renderTabContent,
  renderHeader,
  activeTab,
  setActiveTab,
  liveStreamLocked
}) => {
  const { isMobile, isTablet, device } = useDeviceType();
  const [hidePageRight, setHidePageRight] = useLocalStorage<boolean>('hide-session-page-right');
  const [scroll, setScroll] = useState<number>();
  const [summaryScroll, setSummaryScroll] = useState<number>();
  const [widgetWidth, setWidgetWidth] = useLocalStorage<number | string>('session-page-sides-ratio');

  const videoPlayerRef = useRef<UseVideoPlayer | null>(null);
  const tabWrapperRef = useRef<HTMLDivElement>(null);
  const summaryRef = useRef<HTMLDivElement>(null);

  const onSummaryScroll = () => {
    if (isMobile) return;

    if (summaryRef.current?.scrollTop) {
      setSummaryScroll(summaryRef.current.scrollTop);
    }
  };

  const maxWidgetWidth = device.select({ MOBILE: '100%', TABLET: '100%', DEFAULT: '75%' });

  const minWidgetWidth = device.select({ TABLET: '40%', DEFAULT: '25%' });

  const widgetSize = { width: isMobile || isTablet ? '100%' : widgetWidth || '50%', height: 'auto' };

  const onWidgetResize = (_e, _direction, ref) => {
    setWidgetWidth(ref.offsetWidth);
  };

  const onScroll = () => {
    if (isMobile) return;

    if (tabWrapperRef.current?.scrollTop) {
      setScroll(tabWrapperRef.current.scrollTop);
    }
  };

  const resizeStatus = {
    left: !isMobile && !isTablet
  };

  const handleTabSelection = (value: string) => {
    if (!isMobile) {
      setScroll(0);
    }

    setActiveTab(value);
  };

  const onResize = () => {
    if (scroll) {
      tabWrapperRef.current?.scrollTo({ top: scroll });
    }
    if (summaryScroll) {
      summaryRef.current?.scrollTo({ top: summaryScroll });
    }
  };

  useEffect(() => {
    if (!isMobile && !isTablet && activeTab === 'summary') {
      setActiveTab('transcript');
    }
  }, [isMobile, isTablet]);

  useEffect(() => {
    if (isMobile) {
      setWidgetWidth('100%');
    }
  }, [isMobile]);

  return (
    <>
      {renderHeader()}
      <PageBody>
        <SessionContext.Provider
          value={{
            sessionMembers: {
              owner,
              candidate
            },
            widgetWidth: widgetWidth || '50%',
            videoPlayerRef
          }}
        >
          <PageLeft onScroll={onSummaryScroll} ref={summaryRef}>
            <div
              className={cn('flex flex-1 flex-col rounded', {
                'items-center': hidePageRight
              })}
            >
              {renderPageLeft({ restrictWidth: Boolean(hidePageRight) })}
            </div>
            {hidePageRight && !isMobile && !isTablet && (
              <button
                data-testid='split-page'
                className='focus:ring-indigo absolute right-12 top-4 bg-white p-2 hover:text-indigo-600'
                onClick={() => setHidePageRight(!hidePageRight)}
              >
                <SplitPageSVG />
              </button>
            )}
          </PageLeft>
          {(!hidePageRight || isMobile || isTablet) && (
            <Resizable
              size={widgetSize}
              onResizeStop={onWidgetResize}
              onResize={onResize}
              maxWidth={maxWidgetWidth}
              minWidth={minWidgetWidth}
              enable={resizeStatus}
            >
              <PageRight>
                {liveStreamLocked && (
                  <Alert type='warning' className='m-4' heading='Editing notes currently restricted.'>
                    This interview is currently in progress. To prevent overwrites, editing of the interview notes is
                    currently restricted to study moderators.
                    <br /> Please ensure that only one moderator is editing the document at a time.
                  </Alert>
                )}
                <div className='desktop:h-full flex flex-col overflow-hidden'>
                  <div className='flex items-center justify-between border-b border-gray-200 px-4'>
                    <Tabs
                      current={activeTab}
                      labels={{
                        transcript: 'Transcript',
                        highlights: 'Highlights',
                        notes: 'Notes',
                        ...((isMobile || isTablet) && { summary: 'Summary' })
                      }}
                      onSelect={handleTabSelection}
                      className='flex items-center'
                      tabs={compact([(isMobile || isTablet) && 'summary', 'transcript', 'highlights', 'notes'])}
                    />
                    {!isMobile && !isTablet && (
                      <button
                        data-testid='split-page'
                        className='focus:ring-indigo hover:text-indigo-600'
                        onClick={() => setHidePageRight(!hidePageRight)}
                      >
                        <SplitPageSVG />
                      </button>
                    )}
                  </div>
                  {renderTabContent(activeTab, onScroll, tabWrapperRef)}
                </div>
              </PageRight>
            </Resizable>
          )}
        </SessionContext.Provider>
      </PageBody>
    </>
  );
};
